import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { captureError, addBreadcrumb } from '../../config/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'app' | 'page' | 'component';
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorCount = this.state.errorCount + 1;

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }

    this.setState({
      errorInfo,
      errorCount,
    });

    // If too many errors, show critical error UI
    if (errorCount > 3) {
      this.setState({
        error: new Error('Multiple errors detected. Please refresh the page.'),
      });
    }
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Add breadcrumb for context
    addBreadcrumb('Error boundary caught error', 'error', {
      level: this.props.level || 'component',
      componentStack: errorInfo.componentStack,
    });

    // Send to Sentry with full context
    captureError(error, {
      componentStack: errorInfo.componentStack,
      level: this.props.level || 'component',
      url: window.location.href,
      errorCount: this.state.errorCount + 1,
    });
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      const { level = 'component', showDetails = false } = this.props;
      const { error, errorInfo, errorCount } = this.state;

      // Different UI based on error boundary level
      if (level === 'app') {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-16">
            <div className="max-w-md w-full">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h1 className="mt-4 text-xl font-semibold text-center text-gray-900">
                  Algo salió mal
                </h1>
                <p className="mt-2 text-sm text-center text-gray-600">
                  Ha ocurrido un error inesperado. Puedes intentar recargar la página o volver al inicio.
                </p>

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    onClick={this.handleReload}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-chile-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-chile-blue"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Recargar Página
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-chile-blue"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Ir al Inicio
                  </button>
                </div>

                {showDetails && error && process.env.NODE_ENV === 'development' && (
                  <details className="mt-6">
                    <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                      Detalles del error (desarrollo)
                    </summary>
                    <div className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700">
                      <p className="font-semibold">{error.toString()}</p>
                      {errorInfo && (
                        <pre className="mt-2 whitespace-pre-wrap text-xs">
                          {errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}

                {errorCount > 1 && (
                  <p className="mt-4 text-xs text-center text-gray-500">
                    Errores detectados: {errorCount}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      }

      if (level === 'page') {
        return (
          <div className="bg-white rounded-lg shadow p-6 my-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-gray-800">
                  Error al cargar esta sección
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  No se pudo cargar el contenido correctamente.
                </p>
                <div className="mt-4">
                  <button
                    onClick={this.handleReset}
                    className="text-sm font-medium text-chile-blue hover:text-blue-700"
                  >
                    Intentar de nuevo
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Component level error
      return (
        <div className="border border-red-200 rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
            <div className="ml-2">
              <p className="text-sm text-red-800">
                Error al cargar este componente
              </p>
              <button
                onClick={this.handleReset}
                className="mt-1 text-xs font-medium text-red-600 hover:text-red-500"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
